/*
  Impersonation Interceptor
  Forwards the IMPERSONATION_TOKEN cookie as the Impersonation-Token header on API
  requests, but ONLY from impersonation surfaces — workflow/task, data-request and
  invitation pages — plus requests to the impersonation session endpoint itself (the
  banner needs it everywhere). GP-portal navigation never sends the header, so opted-in
  endpoints shared by both sides (e.g. entities#show for the /e/:eid route shell) keep
  the GP's identity on GP pages instead of swapping and 404ing. The backend allowlist
  remains the security boundary.

  A 403 invalid_impersonation response means the session is stale (expired, revoked,
  or superseded) — the impersonation cookies are cleared and the page reloaded so the
  user continues with their own session. Never triggers a logout.
*/

import { catchError } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { environment } from '@env/environment';
import {
  IMPERSONATION_ORIGINAL_URL_COOKIE,
  IMPERSONATION_TOKEN_COOKIE,
  IMPERSONATION_TOKEN_HEADER,
  INVALID_IMPERSONATION_ERROR
} from '@state/impersonation/impersonation.model';
import { setWindowLocation } from '@helpers/url';

@Injectable({
  providedIn: 'root'
})
export class ImpersonationInterceptor implements HttpInterceptor {
  private cookieService = inject(CookieService);

  // Route segments whose pages act as the impersonated investor. `workflow/` also covers its
  // task child routes; the segment boundary keeps the GP-side `workflows` list unmatched.
  private static readonly IMPERSONATION_SURFACES = /(?:^|\/)(?:workflow|data-request|invitation)(?:\/|$)/;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let token = this.cookieService.get(IMPERSONATION_TOKEN_COOKIE);

    if (token && request.url.startsWith(environment.apiUrl) && this.impersonationScopedRequest(request)) {
      request = request.clone({
        setHeaders: { [IMPERSONATION_TOKEN_HEADER]: token }
      });
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse): Observable<never> => {
        if (err.status === 403 && err.error?.message === INVALID_IMPERSONATION_ERROR) {
          this.clearImpersonationCookies();
          setWindowLocation(window.location.href);
        }

        return throwError(() => err);
      })
    );
  }

  // The session endpoint always gets the header (the banner reads the session from any page);
  // other requests get it only while the user is on an impersonation surface.
  private impersonationScopedRequest(request: HttpRequest<any>): boolean {
    if (request.url.startsWith(`${environment.apiUrl}/v1/impersonation`)) return true;

    return ImpersonationInterceptor.IMPERSONATION_SURFACES.test(window.location.pathname);
  }

  private clearImpersonationCookies(): void {
    let domain = new URL(environment.docsUrl).hostname;

    this.cookieService.delete(IMPERSONATION_TOKEN_COOKIE, '/', domain);
    this.cookieService.delete(IMPERSONATION_ORIGINAL_URL_COOKIE, '/', domain);
  }
}
